import {
    LaneGroupTemperatureRange, LeaseType,
    CollectionServiceType, DeliveryServiceType, TransportModeRoad,
} from 'app/enums/label.enums'
import {
    DTOCardFormField, FieldInCard, NestedField,
} from 'app/types/form.types'
import {
    AccountLaneForm,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.types'
import {
    LaneGroup,
} from 'app/Apps/Pricing/Pricing.types'
import {
    SizeLink,
    TypeLink,
} from 'app/shared-components/SkyNetLink'

import {
    RequestedLane,
} from 'app/Apps/AccountManagement/AQuotes/AQuotes.types'

type AQuoteLaneGroupForm = keyof (LaneGroup & RequestedLane & {
    airlineName: string,
    isPerformanceQualification: boolean,
})

const getFields = ({
    getAllowedValues,
    showFTL,
    showLTL,
    isDeliveryRoad,
    laneGroupLink,
}: {
    getAllowedValues: (val: Record<string, string>) => Record<string, string>,
    showFTL: boolean,
    showLTL: boolean,
    isDeliveryRoad: boolean,
    laneGroupLink: string

}): DTOCardFormField<AQuoteLaneGroupForm>[] => {
    const generalFields: FieldInCard<AQuoteLaneGroupForm>[] = [
        {
            id: 'column1',
            fields: [
                {
                    name: 'tempRange',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LaneGroupTemperatureRange),
                    labelKey: 'LaneGroup.tempRange',
                    disabled: true,
                },
                {
                    name: 'leaseType',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LeaseType),
                    labelKey: 'LaneGroup.leaseType',
                    showInColumns: true,
                },
                {
                    name: 'changedBy',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.changedBy',
                    disabled: true,
                },
                {
                    name: 'changedOn',
                    componentName: 'DateTimeSelect',
                    labelKey: 'AccountLane.changedOn',
                    disabled: true,
                },
            ],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'comment',
                    componentName: 'LongText',
                    labelKey: 'LaneGroup.comment',
                    disabled: true,
                },
                {
                    name: 'laneGroupNumber',
                    componentName: 'SkyNetLink',
                    link: laneGroupLink,
                    labelKey: 'LaneGroup.laneGroupNumber',
                    type: TypeLink.EXTERNAL,
                    size: SizeLink.S,
                },
                {
                    name: 'isPerformanceQualification',
                    componentName: 'Checkbox',
                    labelKey: 'AccountQuotes.isPerformanceQualification',
                    disabled: true,
                },
            ],
        },
    ]

    const transportModeRoad = {
        name: 'transportModeRoad',
        labelKey: 'LaneGroupCreateOrDuplicateDto.transportModeRoad',
        componentName: 'EnumSelector',
        allowedValues: getAllowedValues(TransportModeRoad),
        showInColumns: true,
    } as FieldInCard<AccountLaneForm>

    const isFtlPositioningTemperatureControlled = {
        name: 'isFtlPositioningTemperatureControlled',
        labelKey: 'LaneGroupCreateOrDuplicateDto.isFtlPositioningTemperatureControlled',
        componentName: 'Checkbox',
    } as FieldInCard<AccountLaneForm>

    const isLtlPositioningTemperatureControlled = {
        name: 'isLtlPositioningTemperatureControlled',
        labelKey: 'LaneGroupCreateOrDuplicateDto.isLtlPositioningTemperatureControlled',
        componentName: 'Checkbox',
    } as FieldInCard<AccountLaneForm>

    let transportModeFields = [transportModeRoad]

    if (showLTL) {
        transportModeFields = [
            ...transportModeFields,
            isLtlPositioningTemperatureControlled,
        ]
    }

    if (showFTL) {
        transportModeFields = [
            ...transportModeFields,
            isFtlPositioningTemperatureControlled,
        ]
    }

    const handoverFields: FieldInCard<AQuoteLaneGroupForm>[] = [
        {
            id: 'column1',
            fields: [
                {
                    name: 'deliveryServiceType',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(DeliveryServiceType),
                    labelKey: 'LaneGroup.deliveryServiceType',
                    required: true,
                },
                ...(isDeliveryRoad
                    ? transportModeFields : []
                ) as NestedField<AQuoteLaneGroupForm>[],
            ],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'handoverPoint',
                    componentName: 'LocationObjectSelector',
                    labelKey: 'LaneGroup.handoverPoint',
                },
                {
                    name: 'handoverPointContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'LaneGroup.handoverPointContacts',
                    disabled: true,
                },
            ],
        },
    ]

    const collectionFields: FieldInCard<AQuoteLaneGroupForm>[] = [
        {
            id: 'column1',
            fields: [{
                name: 'collectionServiceType',
                componentName: 'EnumSelector',
                allowedValues: getAllowedValues(CollectionServiceType),
                labelKey: 'LaneGroup.collectionServiceType',
            }],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'collectionDropoffPoint',
                    componentName: 'LocationObjectSelector',
                    labelKey: 'LaneGroup.collectionDropoffPoint',
                },
                {
                    name: 'collectionDropoffPointContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'LaneGroup.collectionDropoffPointContacts',
                    disabled: true,
                },
            ],
        },
    ]

    return [
        {
            id: 'general',
            title: 'General Information',
            className: 'general',
            fields: generalFields,
        },
        {
            id: 'handover',
            title: 'Handover',
            className: 'handover',
            fields: handoverFields,
        },
        {
            id: 'collection',
            title: 'Collection',
            className: 'collection',
            fields: collectionFields,
        },
    ]
}

export default getFields
